import React, { useState } from 'react';
import {
  RewardWindowFields, RewardWindowValue, EMPTY_WINDOW,
  windowError, toWindowPayload, fromReward, windowSummary,
} from './RewardWindowFields';

import { Env } from '../../shared';
import { BusinessPicker, PickedBusiness } from '../../create/shared/BusinessPicker';
import {
  useVenueRewards,
  OutOfBoundsError,
  TEMPLATE_KEYS,
  TEMPLATE_LABELS,
  TemplateKey,
  COINS_MIN,
  COINS_MAX,
  VenueReward,
} from '../api/useVenueRewards';

/**
 * Venue reward catalog — create and manage the rewards users redeem with coins.
 *
 * Admin-created rewards go live immediately (approved + active): they are only
 * created after the business has confirmed them, so the moderation queue that
 * owner-submitted custom rewards pass through would be approving our own work.
 *
 * Enrollment is shown first and inline, because a reward on an unenrolled venue
 * is invisible in the app with nothing on screen to explain why.
 */

const inputCls =
  'w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-white ' +
  'placeholder-gray-600 focus:outline-none focus:border-violet-500';

function Pill({ tone, children }: { tone: 'on' | 'off' | 'warn'; children: React.ReactNode }) {
  const cls = {
    on: 'bg-emerald-950/50 border-emerald-800/60 text-emerald-300',
    off: 'bg-gray-800 border-gray-700 text-gray-400',
    warn: 'bg-amber-950/50 border-amber-800/60 text-amber-300',
  }[tone];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-semibold ${cls}`}>
      {children}
    </span>
  );
}

export function VenueRewardsPanel({ env }: { env: Env }) {
  const [business, setBusiness] = useState<PickedBusiness | null>(null);
  const {
    venue, items, loading, error, actionError,
    create, update, remove, setEnrollment,
  } = useVenueRewards(env, business?.id ?? null);

  const [mode, setMode] = useState<'template' | 'custom'>('template');
  const [templateKey, setTemplateKey] = useState<TemplateKey>(TEMPLATE_KEYS[0]);
  const [description, setDescription] = useState('');
  const [coinsCost, setCoinsCost] = useState('100');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<VenueReward | null>(null);
  const [windowOn, setWindowOn] = useState(false);
  const [windowVal, setWindowVal] = useState<RewardWindowValue>(EMPTY_WINDOW);
  // The edit modal carries its own copy so cancelling leaves the reward alone.
  const [editWindowOn, setEditWindowOn] = useState(false);
  const [editWindowVal, setEditWindowVal] = useState<RewardWindowValue>(EMPTY_WINDOW);

  const cost = Number(coinsCost);
  const costValid = Number.isFinite(cost) && cost > 0;
  const outOfRange = costValid && (cost < COINS_MIN || cost > COINS_MAX);
  const canSubmit =
    Boolean(business) && costValid && (mode === 'template' || description.trim().length > 0)
    && windowError(windowOn, windowVal) === null;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    const input = {
      templateKey: mode === 'template' ? templateKey : null,
      description: mode === 'custom' ? description.trim() : TEMPLATE_LABELS[templateKey],
      coinsCost: cost,
      ...toWindowPayload(windowOn, windowVal),
    };
    try {
      await create(input);
      setDescription('');
      setWindowOn(false);
      setWindowVal(EMPTY_WINDOW);
    } catch (e) {
      if (e instanceof OutOfBoundsError) {
        const ok = window.confirm(
          `${cost} coins is outside the usual ${COINS_MIN}–${COINS_MAX} range.\n\n` +
            'Create it anyway?',
        );
        if (ok) await create({ ...input, allowOutOfBounds: true });
      }
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    const patch = {
      coinsCost: editing.coins_cost,
      description: editing.description,
      ...toWindowPayload(editWindowOn, editWindowVal),
    };
    try {
      const ok = await update(editing.id, patch);
      if (ok) setEditing(null);
    } catch (e) {
      if (e instanceof OutOfBoundsError) {
        const confirmed = window.confirm(
          `${editing.coins_cost} coins is outside the usual ${COINS_MIN}–${COINS_MAX} range.\n\n` +
            'Save anyway?',
        );
        if (confirmed) {
          const ok = await update(editing.id, { ...patch, allowOutOfBounds: true });
          if (ok) setEditing(null);
        }
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="max-w-md">
        <BusinessPicker env={env} value={business} onChange={setBusiness} />
      </div>

      {!business && (
        <p className="text-sm text-gray-500">
          Pick a venue to see and manage the rewards its customers can redeem.
        </p>
      )}

      {error && (
        <div className="px-3 py-2 bg-red-950/40 border border-red-900/50 rounded-lg text-sm text-red-300">
          {error}
        </div>
      )}
      {actionError && (
        <div className="px-3 py-2 bg-red-950/40 border border-red-900/50 rounded-lg text-sm text-red-300">
          {actionError}
        </div>
      )}

      {venue && (
        <>
          {/* Enrollment */}
          <div className="flex items-center gap-3 flex-wrap px-4 py-3 bg-gray-900 border border-gray-800 rounded-xl">
            <span className="text-sm text-white font-medium">{venue.name}</span>
            {venue.enrollment_active ? (
              <Pill tone="on">Enrolled in gamification</Pill>
            ) : (
              <Pill tone="warn">
                {venue.is_enrolled ? 'Enrollment switched off' : 'Not enrolled'}
              </Pill>
            )}
            {!venue.enrollment_active && (
              <span className="text-xs text-gray-500">
                Rewards stay invisible to users until this venue is enrolled.
              </span>
            )}
            <button
              onClick={() => setEnrollment(!venue.enrollment_active)}
              disabled={busy}
              className="ml-auto px-3 py-1.5 text-xs font-medium text-gray-200 bg-gray-800 border border-gray-700 rounded-lg hover:border-gray-500 disabled:opacity-50"
            >
              {venue.enrollment_active ? 'Unenroll' : 'Enroll venue'}
            </button>
          </div>

          {/* Create */}
          <div className="px-4 py-4 bg-gray-900 border border-gray-800 rounded-xl space-y-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-white">Add a reward</h3>
              <div className="ml-auto flex gap-1">
                {(['template', 'custom'] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => setMode(m)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md border capitalize ${
                      mode === m
                        ? 'bg-violet-950/40 border-violet-600 text-violet-200'
                        : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                {mode === 'template' ? (
                  <select
                    value={templateKey}
                    onChange={(e) => setTemplateKey(e.target.value as TemplateKey)}
                    className={inputCls}
                  >
                    {TEMPLATE_KEYS.map((k) => (
                      <option key={k} value={k}>
                        {TEMPLATE_LABELS[k]}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={200}
                    placeholder="e.g. Free appetizer with any entrée"
                    className={inputCls}
                  />
                )}
              </div>
              <div>
                <input
                  type="number"
                  value={coinsCost}
                  onChange={(e) => setCoinsCost(e.target.value)}
                  placeholder="Coins"
                  className={inputCls}
                />
              </div>
            </div>

            <div className="pt-1 border-t border-gray-800">
              <RewardWindowFields
                idPrefix="create"
                enabled={windowOn}
                value={windowVal}
                onEnabledChange={setWindowOn}
                onChange={setWindowVal}
              />
            </div>

            <div className="flex items-center gap-3">
              {outOfRange && (
                <span className="text-xs text-amber-300">
                  Outside the usual {COINS_MIN}–{COINS_MAX} range — you'll be asked to confirm.
                </span>
              )}
              <button
                onClick={submit}
                disabled={!canSubmit || busy}
                className="ml-auto px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-pink-600 rounded-lg hover:opacity-90 disabled:opacity-40"
              >
                Add reward
              </button>
            </div>
          </div>

          {/* Catalog */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-gray-800 flex items-center gap-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Catalog
              </p>
              <span className="text-xs text-gray-600">{items.length} reward(s)</span>
            </div>

            {loading && (
              <p className="px-4 py-10 text-center text-sm text-gray-500">Loading…</p>
            )}
            {!loading && items.length === 0 && (
              <p className="px-4 py-10 text-center text-sm text-gray-500">
                No rewards yet for this venue.
              </p>
            )}

            {items.map((r) => (
              <div
                key={r.id}
                className="flex items-center gap-3 flex-wrap px-4 py-3 border-b border-gray-800 last:border-b-0"
              >
                {editing?.id === r.id ? (
                  <>
                    <input
                      value={editing.description}
                      onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                      className={`${inputCls} flex-1 min-w-[180px]`}
                    />
                    <input
                      type="number"
                      value={editing.coins_cost}
                      onChange={(e) =>
                        setEditing({ ...editing, coins_cost: Number(e.target.value) })
                      }
                      className={`${inputCls} w-28`}
                    />
                    <div className="w-full">
                      <RewardWindowFields
                        idPrefix={`edit-${r.id}`}
                        enabled={editWindowOn}
                        value={editWindowVal}
                        onEnabledChange={setEditWindowOn}
                        onChange={setEditWindowVal}
                      />
                    </div>
                    <button
                      onClick={saveEdit}
                      disabled={busy || windowError(editWindowOn, editWindowVal) !== null}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-violet-600 rounded-lg hover:opacity-90 disabled:opacity-40"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditing(null)}
                      className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{r.description}</p>
                      <p className="text-xs text-gray-500">
                        {r.template_key ? `Template · ${r.template_key}` : 'Custom'}
                        {r.redemption_count > 0 && ` · ${r.redemption_count} redeemed`}
                      </p>
                      {windowSummary(r) && (
                        <p className="text-xs text-violet-300/80">🕒 {windowSummary(r)}</p>
                      )}
                    </div>
                    <span className="text-sm text-amber-300 font-semibold tabular-nums">
                      {r.coins_cost}
                    </span>
                    {r.is_active ? <Pill tone="on">Active</Pill> : <Pill tone="off">Inactive</Pill>}
                    {r.moderation_status !== 'approved' && (
                      <Pill tone="warn">{r.moderation_status}</Pill>
                    )}
                    <button
                      onClick={() => {
                        const w = fromReward(r);
                        setEditWindowOn(w.enabled);
                        setEditWindowVal(w.value);
                        setEditing(r);
                      }}
                      className="px-2.5 py-1 text-xs text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:border-gray-500"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => update(r.id, { isActive: !r.is_active })}
                      disabled={busy}
                      className="px-2.5 py-1 text-xs text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:border-gray-500 disabled:opacity-40"
                    >
                      {r.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => {
                        // A redeemed reward cannot be deleted — the redemption FK
                        // would orphan. The backend enforces it; say so up front.
                        if (r.redemption_count > 0) {
                          window.alert(
                            `This reward has ${r.redemption_count} redemption(s) and cannot be ` +
                              'deleted. Deactivate it instead.',
                          );
                          return;
                        }
                        if (window.confirm(`Delete "${r.description}"?`)) remove(r.id);
                      }}
                      className="px-2.5 py-1 text-xs text-red-300 bg-red-950/40 border border-red-900/50 rounded-md hover:border-red-700"
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
